<template>
    <editor-content :editor="editor" />
</template>

<script setup>
import { Document } from '@tiptap/extension-document';
import { Gapcursor } from '@tiptap/extension-gapcursor';
import { Text } from '@tiptap/extension-text';
import { Bold } from '@tiptap/extension-bold';
import { Strike } from '@tiptap/extension-strike';
import { Italic } from '@tiptap/extension-italic';
import { Underline } from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Heading } from '@tiptap/extension-heading';
import { ListItem } from '@tiptap/extension-list-item';
import { TextAlign } from '@tiptap/extension-text-align';
import { OrderedList } from '@tiptap/extension-ordered-list';
import { BulletList } from '@tiptap/extension-bullet-list';
import { FontFamily } from '@tiptap/extension-font-family';
import { Highlight } from '@tiptap/extension-highlight';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { Dropcursor } from '@tiptap/extension-dropcursor';
import { HardBreak } from '@tiptap/extension-hard-break';
import { Blockquote } from '@tiptap/extension-blockquote';
import { History } from '@tiptap/extension-history';
import { Color } from '@tiptap/extension-color';
import { Typography } from '@tiptap/extension-typography';
import { Editor, EditorContent } from '@tiptap/vue-3';
import IteratorStartComponent from '../composables/useIteratorStartExtension';
import IteratorEndComponent from '../composables/useIteratorEndExtension';
import { inject, onBeforeUnmount } from 'vue';

const editorExt = inject('useWYSIWYGEditorExtensions');

defineExpose({
    name: 'WYSIWYGEditorCore',
});

const props = defineProps({
    modelValue: {
        type: String,
        default: '',
    },
});

const emit = defineEmits([
    'updateContent:modelValue',
    'focusContent:modelValue',
    'blurContent:modelValue',
]);

const modelValue = props.modelValue;
const activeComponents = inject('activeComponents');
const templateEditModel = inject('templateEditModel');

const callEditor = () => {
    // Ft: verbose style config needed
    const editorInstance = new Editor({
        //element: document.querySelector(`#${element}`),
        editorProps: {
            attributes: {
                class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-hidden',
            },
            transformPastedText(str) {
                return editorExt.sanitizeHTML(str);
            },
        },
        injectCSS: false,
        extensions: [
            Document,
            editorExt.CustomParagraph.configure({}),
            Dropcursor.configure({
                width: 1,
                color: '#FF0000',
            }),
            editorExt.PageBreak.configure({}),
            Gapcursor,
            Blockquote,
            Text,
            Underline,
            Bold,
            Strike,
            Italic,
            Heading,
            editorExt.CustomLink.configure({
                openOnClick: false,
                autolink: false,
                defaultProtocol: 'https',
                linkOnPaste: false,
                protocols: [
                    {
                        scheme: 'tel',
                        optionalSlashes: false,
                    },
                    {
                        scheme: 'mailto',
                        optionalSlashes: false,
                    },
                    {
                        scheme: 'http',
                        optionalSlashes: true,
                    },
                ],
                HTMLAttributes: {
                    target: '_blank',
                },
            }),
            Color,
            /* Image.configure({
                inline: true,
                allowBase64: true,
            }),  */
            /*
            Resizable,
            ImageResize.configure({
                inline: false,
                allowBase64: true,
            }), */
            editorExt.CustomImage.configure({
                inline: true,
                allowBase64: true,
            }),
            editorExt.CustomLine.configure({
                HTMLAttributes: {
                    class: 'tpl-hr',
                },
            }),
            ListItem,
            OrderedList,
            BulletList,
            TextStyle,
            Typography,
            TextAlign.configure({
                defaultAlignment: 'left',
                types: ['heading', 'paragraph', 'table', 'table-cell'],
            }),
            FontFamily,
            Highlight.configure({ multicolor: true }),
            Subscript,
            Superscript,
            HardBreak,
            editorExt.CustomTable.configure({
                resizable: true,
                lastColumnResizable: true,
                allowTableNodeSelection: true,
            }),
            editorExt.CustomTableRow.configure({}),
            editorExt.CustomTableHeader.configure({}),
            editorExt.CustomTableCell.configure({
                cellMinWidth: 10,
            }),
            editorExt.TableWidthLimiter,
            History.configure({
                depth: 200,
            }),
            IteratorStartComponent,
            IteratorEndComponent,
            // Code,
            //editorExt.CustomCode,
            editorExt.CodeHighlighter,
        ],
        content: modelValue,
        /* onCreate: ({ editor }) => {

        }, */
        onUpdate: ({ editor }) => {
            emit('updateContent:modelValue', activeComponents, templateEditModel, editor);
        },
        onFocus({ editor }) {
            // The editor is focused.
            emit('focusContent:modelValue', activeComponents, editor);
        },
        /* onBlur({ editor }) {
            // The editor isn’t focused anymore.
            emit('blurContent:modelValue', activeComponents, editor);
        }, */
    });
    return editorInstance;
};

const editor = callEditor();

onBeforeUnmount(() => {
    editor.destroy();
});
</script>
